import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { useNetworkMonitor } from "libs/network-monitor/useNetworkMonitor";
import { isEqual } from "lodash-es";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import store from "redux/store";
import { getDatabaseSize } from "utils/indexedDb";

interface ContextProps {
  isOnline: boolean;
  timeBeginOffline: number | undefined;
  offlineEditedDataSize: number | undefined;
  recomputeDataSize: () => void;
  setIsOnline: (isOnline: boolean) => Promise<void>;
}

export const OfflineContext = createContext<ContextProps>({
  isOnline: true,
  timeBeginOffline: undefined,
  offlineEditedDataSize: undefined,
  recomputeDataSize: () => {},
  setIsOnline: () => Promise.resolve(),
});

export const useAppNetwork = () => {
  return useContext(OfflineContext);
};

export const useRecomputeDataSize = (data: any[]) => {
  const { recomputeDataSize } = useAppNetwork();

  useEffect(() => {
    recomputeDataSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, data);
};

export const OfflineProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [offlineEditedDataSize, setOfflineEditedDataSize] = useState<
    number | undefined
  >(undefined);
  const { isOnline, timeBeginOffline } = useNetworkMonitor(
    (state) => ({
      isOnline: state.isOnline,
      timeBeginOffline: state.timeBeginOffline,
    }),
    isEqual
  );

  const timeoutRef = useRef<any>();

  const setIsOnline = useCallback(async (isOnline: boolean) => {
    networkMonitor.updateStatus(isOnline);
  }, []);

  const recomputeDataSize = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      getDatabaseSize().then((value) => {
        setOfflineEditedDataSize(value);
      });
    }, 200);
  }, []);

  useEffect(() => {
    const unSubscribe = store.subscribe(() => {
      recomputeDataSize();
    });
    recomputeDataSize();

    return () => {
      unSubscribe();
    };
  }, [isOnline, recomputeDataSize]);

  const value = useMemo(
    () => ({
      offlineEditedDataSize,
      recomputeDataSize,
      isOnline,
      timeBeginOffline,
      setIsOnline,
    }),
    [
      offlineEditedDataSize,
      isOnline,
      timeBeginOffline,
      recomputeDataSize,
      setIsOnline,
    ]
  );

  if (isOnline === undefined) {
    return <></>;
  }

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
};
